import { useEventCallback } from '@mui/material/utils';
import { pick } from 'lodash';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useHistoryStack } from '@work4all/components/lib/navigation/history-stack';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { LedgerAccount } from '@work4all/models/lib/Classes/LedgerAccount.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { CustomValidationRules } from '@work4all/utils/lib/form-utils/jsonSchemaResolver';

import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';

import { LedgerAccountMaskContent } from './components/ledger-account-mask-content/LedgerAccountMaskContent';
import { useExistingledgerAccounts } from './hooks/use-existing-ledger-accounts';
import { useLedgerAccountRequestData } from './hooks/use-ledger-account-request-data';
import { LedgerAccountFormValue } from './types';

type LedgerAccountOverlayControllerProps = MaskControllerProps & {
  amplitudeEntryPoint: string;
};

const DEFAULT_VALUES: LedgerAccount = {
  depositAccount: 0,
  substituteAccountVatConversion: 0,
  taxKeyValue: 0,
};

export function LedgerAccountOverlayController(
  props: LedgerAccountOverlayControllerProps
) {
  const { amplitudeEntryPoint } = props;

  const { goBack } = useHistoryStack();

  const [mutate] = useDataMutation<
    LedgerAccount,
    EMode.upsert,
    {
      attachements;
    }
  >({
    entity: Entities.ledgerAccount,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
    },
  });

  const mask = useMaskConfig(props);

  useEffect(() => {
    if (mask.isCreateMode) {
      sendAmplitudeData(EventType.AddLedgerAccount, {
        entryPoint: amplitudeEntryPoint,
      });
    } else {
      sendAmplitudeData(EventType.EditLedgerAccount, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const request = useLedgerAccountRequestData(mask.id);

  const customRules: CustomValidationRules = () => {
    if (existingLedgerAccountsRes.data.length > 0) {
      return {
        number: {
          message: t('ERROR.ALREADY_EXISTS', { item: t('COMMON.NUMBER') }),
          type: 'customValidation',
        },
      };
    }

    return true;
  };

  const overlay = useMaskOverlay<LedgerAccountFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => x.name,
    customRules,
  });

  const { formState } = overlay.form;

  const number = overlay.form.watch('number');
  const existingLedgerAccountsRes = useExistingledgerAccounts({
    number,
    id: Number(mask.id),
  });

  const { t } = useTranslation();

  const handleSubmit = useEventCallback(
    async (values: LedgerAccountFormValue) => {
      const update = mask.isCreateMode
        ? values
        : pick(values, ['id', ...Object.keys(formState.dirtyFields)]);

      await mutate(update);

      sendAmplitudeData(EventType.SaveLedgerAccount, {
        entryPoint: amplitudeEntryPoint,
      });
      goBack({ force: true });
    }
  );

  return (
    <OverlayController<LedgerAccountFormValue>
      {...overlay}
      onSubmit={handleSubmit}
    >
      <LedgerAccountMaskContent />
    </OverlayController>
  );
}
